import { useEffect, useMemo, useState } from 'react';
import { getUserPreferences, setPreference, deletePreference } from '@/services/platform/data/preferences';
import type { PreferenceDto } from '@/services/platform/api/model';

/** The subset of venue fields needed to bookmark it as a user preference. */
export interface BookmarkableVenue {
  name?: string | null;
  preferenceNodeKey?: string | null;
  preferenceCategoryNodeKey?: string | null;
  preferenceCategoryName?: string | null;
  preferenceSubcategoryName?: string | null;
}

export interface UseVenueBookmarksResult {
  isBookmarked: (venue: BookmarkableVenue) => boolean;
  toggleBookmark: (venue: BookmarkableVenue) => Promise<void>;
}

/**
 * Fetches the user's venue preferences and exposes bookmark state + toggling for any venue-like
 * object. Pass `enabled: false` to skip the fetch until needed (e.g. a sheet that only loads
 * preferences while open).
 */
export function useVenueBookmarks(options?: { enabled?: boolean }): UseVenueBookmarksResult {
  const enabled = options?.enabled ?? true;
  const [preferences, setPreferences] = useState<PreferenceDto[]>([]);

  useEffect(() => {
    if (!enabled) return;
    getUserPreferences().then(prefs => {
      if (prefs) setPreferences(prefs);
    });
  }, [enabled]);

  const bookmarkedKeys = useMemo(
    () => new Set(preferences.map(p => p.subcategoryNodeKey).filter(Boolean) as string[]),
    [preferences]
  );

  function isBookmarked(venue: BookmarkableVenue): boolean {
    return !!venue.preferenceNodeKey && bookmarkedKeys.has(venue.preferenceNodeKey);
  }

  async function toggleBookmark(venue: BookmarkableVenue): Promise<void> {
    if (!venue.preferenceNodeKey || !venue.preferenceCategoryNodeKey) return;
    const preferenceKey = venue.preferenceNodeKey;
    const existing = preferences.find(p => p.subcategoryNodeKey === preferenceKey);
    if (existing?.publicGuid) {
      const removed = await deletePreference(existing.publicGuid);
      if (removed) setPreferences(prev => prev.filter(p => p.subcategoryNodeKey !== preferenceKey));
    } else {
      const saved = await setPreference({
        categoryNodeKey: venue.preferenceCategoryNodeKey,
        category: venue.preferenceCategoryName ?? undefined,
        subcategoryNodeKey: preferenceKey,
        subcategory: venue.preferenceSubcategoryName ?? undefined,
        value: venue.name ?? '',
      });
      if (saved) setPreferences(prev => [...prev, saved]);
    }
  }

  return { isBookmarked, toggleBookmark };
}
